import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { productsAPI, cartAPI } from '../lib/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { savePendingCartItem, dispatchCartUpdate } from '../lib/pendingCart';
import { splitProductDescription } from '../lib/productDescription';
import ProductVendorTermsNotice from '../components/ProductVendorTermsNotice';

const ProductPageSolutionCopy = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productsAPI.getProduct(id);
        setProduct(data);
      } catch (e) {
        setError(e.message || 'Ошибка при загрузке решения');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id]);

  const isNotEmpty = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  };

  const hero = product?.hero || {};
  const heroBadgeText = hero.badge_text || (product?.category === 'ai' ? 'AI-решение' : 'Решение для бизнеса');
  const heroTitle = hero.title || product?.name || 'Название решения';
  const heroSubtitle = hero.subtitle || product?.short_description || 'Короткий оффер решения';
  const heroSummary =
    hero.summary ||
    product?.ai_summary ||
    product?.full_description ||
    product?.short_description ||
    '';

  const descriptionBlocks = useMemo(() => product?.description?.blocks || product?.description_blocks || [], [product?.description?.blocks, product?.description_blocks]);
  const benefits = product?.benefits || [];
  const featuresV2 = product?.features_v2 || [];
  const howItWorksSteps = product?.how_it_works_steps || [];
  const integrations = product?.integrations || [];
  const faqItems = product?.faq_items || [];
  const importSubstitution = useMemo(
    () => (Array.isArray(product?.import_substitution) ? product.import_substitution.filter(Boolean) : []),
    [product?.import_substitution]
  );
  const finalCta = product?.final_cta || {};
  const fullDescription = product?.full_description || '';
  const { mainHtml: fullDescriptionHtml } = splitProductDescription(fullDescription);
  const isFullDescriptionEmpty =
    !fullDescriptionHtml ||
    fullDescriptionHtml.replace(/<[^>]*>/g, '').trim().length === 0;
  const hasDetailBlocks = (!isFullDescriptionEmpty || isNotEmpty(descriptionBlocks)) && (descriptionBlocks.length > 0 || !isFullDescriptionEmpty);
  const hasContent = hasDetailBlocks || isNotEmpty(benefits) || isNotEmpty(featuresV2) || isNotEmpty(howItWorksSteps) || isNotEmpty(integrations) || isNotEmpty(faqItems) || isNotEmpty(importSubstitution);

  const licenses = useMemo(() => {
    if (Array.isArray(product?.licenses) && product.licenses.length > 0) return product.licenses;
    if (Array.isArray(product?.calculator_config?.licenses) && product.calculator_config.licenses.length > 0) return product.calculator_config.licenses;
    return [
      { name: 'Базовая лицензия', price: product?.price || 'по запросу', description: 'Стартовый комплект для быстрого запуска' },
      { name: 'Расширенная лицензия', price: 'по запросу', description: 'Для команд и расширенного внедрения' },
    ];
  }, [product]);

  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(0);
  const selectedLicense = licenses[selectedLicenseIndex];
  const selectedLicensePrice = selectedLicense?.price === 'по запросу' ? 'по запросу' : (selectedLicense?.price || 'по запросу');

  const [contactForm, setContactForm] = useState({ contact: '', contactType: 'email' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { toast } = useToast();

  const formatPrice = (priceStr) => {
    if (!priceStr || priceStr === 'по запросу') return 'по запросу';
    const num = String(priceStr).replace(/[^\d]/g, '');
    if (!num) return priceStr;
    return parseInt(num, 10).toLocaleString('ru-RU') + ' ₽';
  };

  const composition = useMemo(() => {
    const items = [];
    if (selectedLicense?.name) items.push(selectedLicense.name);
    if (selectedLicense?.description) items.push(selectedLicense.description);
    return items.length ? items : ['Лицензия', 'Внедрение', 'Поддержка'];
  }, [selectedLicense]);

  const detailBlocks = useMemo(() => {
    const blocks = [];
    if (!isFullDescriptionEmpty) {
      blocks.push({ title: 'Описание', value: fullDescriptionHtml, isHtml: true });
    }
    if (descriptionBlocks.length) {
      blocks.push({ title: 'Детали', value: descriptionBlocks.map((b) => b.text || b.title || 'Блок описания') });
    }
    return blocks;
  }, [descriptionBlocks, fullDescriptionHtml, isFullDescriptionEmpty]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!contactForm.contact.trim()) return;
    setIsSubmitting(true);
    try {
      await productsAPI.submitRequest({
        product_id: product?.id,
        product_name: product?.name,
        license_name: selectedLicense?.name,
        contact: contactForm.contact,
        contact_type: contactForm.contactType,
        source: 'product_page',
      });
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    setIsAddingToCart(true);
    try {
      const cartItemData = {
        license_name: selectedLicense?.name,
        license_price: selectedLicensePrice,
        quantity: 1,
      };
      
      try {
        await cartAPI.addToCart(product.id, cartItemData);
      } catch (apiError) {
        const status = apiError.response?.status;
        const isAuthError = !status || status === 401 || status === 403;
        
        if (isAuthError) {
          // Guest user - save to pending cart
          savePendingCartItem(product.id, cartItemData);
          dispatchCartUpdate();
          toast({
            title: "Добавлено в корзину",
            description: "Войдите в аккаунт для оформления заказа",
            action: (
              <Button size="sm" onClick={() => navigate('/login', { state: { from: window.location.pathname } })} className="bg-accent hover:bg-accent/90">
                Войти
              </Button>
            ),
          });
          setIsAddingToCart(false);
          return;
        }
        throw apiError;
      }
      
      toast({
        title: "Добавлено в корзину",
        description: "Перейдите в корзину для оформления",
        action: (
          <Button size="sm" onClick={() => window.location.href = '/dashboard?tab=cart'} className="bg-accent hover:bg-accent/90">
            Оформить
          </Button>
        ),
      });
    } catch (error) {
      console.error('Add to cart error:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось добавить в корзину",
        variant: "destructive",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-center"><div className="text-2xl font-semibold mb-2">Загрузка решения…</div><p className="text-muted-foreground">Пожалуйста, подождите</p></div></div>;
  }

  if (error || !product) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-center"><div className="text-2xl font-semibold mb-2 text-destructive">Ошибка загрузки</div><p className="text-muted-foreground">{error || 'Решение не найдено'}</p></div></div>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead title={`${heroTitle} | ${product?.name} | Антология`} description={heroSubtitle} />
      <Header />
      <main className="pt-20 pb-12">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-[1fr_360px] gap-8">
            <div className="space-y-4 max-w-3xl">
              <Badge variant="secondary" className="w-fit">{heroBadgeText}</Badge>
              <h1 className="text-4xl md:text-5xl font-bold">{heroTitle}</h1>
              <p className="text-lg text-muted-foreground">{heroSubtitle}</p>
              {(product?.tags?.length > 0) && (
                <div className="flex flex-wrap gap-2">{product.tags.map((tag) => <Badge key={tag} variant="outline">{tag}</Badge>)}</div>
              )}
              {isNotEmpty(heroSummary) && <div className="rounded-2xl border border-border/50 bg-muted/20 p-4 text-sm text-muted-foreground">{heroSummary}</div>}

              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Выбор лицензии</h2>
                    <Badge variant="secondary">{licenses.length} варианта</Badge>
                  </div>
                  <div className="grid gap-2">
                    {licenses.map((license, index) => (
                      <button
                        key={license.name || index}
                        type="button"
                        onClick={() => setSelectedLicenseIndex(index)}
                        className={`text-left rounded-2xl border p-3 transition-all ${selectedLicenseIndex === index ? 'border-accent bg-accent/5' : 'border-border/50 bg-background/40 hover:border-accent/40'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium">{license.name}</div>
                            {license.description && <div className="mt-1 text-sm text-muted-foreground">{license.description}</div>}
                          </div>
                          <div className="text-sm font-semibold text-accent">{formatPrice(license.price)}</div>
                        </div>
                        {selectedLicenseIndex === index && <div className="mt-2 text-xs uppercase tracking-wide text-accent">Выбрано</div>}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {hasContent && (
                <section className="pt-6 border-t border-border/50">
                  <Tabs defaultValue={
                    hasDetailBlocks ? 'description'
                      : isNotEmpty(benefits) ? 'benefits'
                        : isNotEmpty(featuresV2) ? 'functions'
                          : isNotEmpty(howItWorksSteps) ? 'how'
                            : isNotEmpty(integrations) ? 'integration'
                              : isNotEmpty(importSubstitution) ? 'substitution'
                                : 'faq'
                  }>
                    <div className="sticky top-4 z-20 bg-background/90 backdrop-blur border border-border/40 rounded-2xl p-2 mb-6">
                      <TabsList className="grid grid-cols-3 lg:grid-cols-7 gap-2">
                        {hasDetailBlocks && <TabsTrigger value="description">Описание</TabsTrigger>}
                        {isNotEmpty(benefits) && <TabsTrigger value="benefits">Преимущества</TabsTrigger>}
                        {isNotEmpty(featuresV2) && <TabsTrigger value="functions">Функции</TabsTrigger>}
                        {isNotEmpty(howItWorksSteps) && <TabsTrigger value="how">Как работает</TabsTrigger>}
                        {isNotEmpty(integrations) && <TabsTrigger value="integration">Интеграции</TabsTrigger>}
                        {isNotEmpty(importSubstitution) && <TabsTrigger value="substitution">Импортозамещение</TabsTrigger>}
                        {isNotEmpty(faqItems) && <TabsTrigger value="faq">FAQ</TabsTrigger>}
                      </TabsList>
                    </div>

                    {hasDetailBlocks && (
                      <TabsContent value="description">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            {fullDescriptionHtml && isNotEmpty(fullDescriptionHtml) ? (
                              <>
                                <div
                                  className="prose prose-sm sm:prose-base max-w-none text-foreground"
                                  dangerouslySetInnerHTML={{ __html: fullDescriptionHtml }}
                                />
                                <ProductVendorTermsNotice className="mt-6 mb-0" />
                              </>
                            ) : (
                              <div className="grid md:grid-cols-2 gap-3">
                                {detailBlocks.map((block) => (
                                  <div key={block.title} className="rounded-2xl border border-border/50 bg-background/40 p-4">
                                    <h3 className="text-base font-semibold mb-2">{block.title}</h3>
                                    {Array.isArray(block.value) ? (
                                      <ul className="space-y-2 text-sm text-muted-foreground">
                                        {block.value.map((item) => <li key={item} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent shrink-0" /><span>{item}</span></li>)}
                                      </ul>
                                    ) : <p className="text-sm text-muted-foreground">{block.value}</p>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(benefits) && (
                      <TabsContent value="benefits">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">Преимущества</h2>
                            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                              {benefits.map((item, idx) => (
                                <div key={idx} className="rounded-2xl border border-border/50 bg-muted/20 p-4 text-sm font-medium">
                                  {item.title || item.description || 'Преимущество'}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(featuresV2) && (
                      <TabsContent value="functions">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">Функции</h2>
                            <div className="grid md:grid-cols-2 gap-2">
                              {featuresV2.map((item, idx) => (
                                <div key={idx} className="rounded-xl border border-border/40 p-3.5 bg-background/40 text-sm">
                                  {item.title || item.description || 'Функция'}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(howItWorksSteps) && (
                      <TabsContent value="how">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">Как работает</h2>
                            <div className="grid md:grid-cols-5 gap-2">
                              {howItWorksSteps.map((item, idx) => (
                                <div key={idx} className="rounded-xl border border-border/40 bg-muted/20 p-3.5 text-sm">
                                  <div className="font-semibold mb-1">{idx + 1}</div>
                                  <div>{item.title || item.description || 'Шаг'}</div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(integrations) && (
                      <TabsContent value="integration">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">Интеграции</h2>
                            <div className="flex flex-wrap gap-2">
                              {integrations.map((item, idx) => <Badge key={idx} variant="outline">{item.name || 'Интеграция'}</Badge>)}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(importSubstitution) && (
                      <TabsContent value="substitution">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">Импортозамещение</h2>
                            <p className="text-sm text-muted-foreground mb-4">
                              Решение замещает следующие программы:
                            </p>
                            <ul className="space-y-2">
                              {importSubstitution.map((program) => (
                                <li key={program} className="flex items-start gap-2 text-sm">
                                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                                  <span>{program}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}

                    {isNotEmpty(faqItems) && (
                      <TabsContent value="faq">
                        <Card>
                          <CardContent className="p-5 md:p-6">
                            <h2 className="text-2xl md:text-3xl font-bold mb-4">FAQ</h2>
                            <div className="space-y-2">
                              {faqItems.map((item, idx) => (
                                <div key={idx} className="rounded-2xl border border-border/50 bg-background/40 p-3.5">
                                  <div className="font-medium mb-1.5">{item.question}</div>
                                  <div className="text-sm text-muted-foreground">{item.answer}</div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}
                  </Tabs>
                </section>
              )}
            </div>

            <div className="lg:sticky lg:top-[88px] lg:self-start">
              <Card className="bg-card/80 border-border/50 shadow-lg">
                <CardContent className="p-5 space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Цена</div>
                    <div className="text-3xl font-bold text-accent">{formatPrice(selectedLicensePrice)}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium">Состав</div>
                    <ul className="space-y-2">
                      {composition.map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <Button type="button" className="w-full h-11 bg-[#F08517] hover:bg-[#2B7AFE] text-white font-medium transition-colors" onClick={handleAddToCart} disabled={isAddingToCart}>
                      {isAddingToCart ? 'Добавляю...' : 'Добавить в корзину'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {isNotEmpty(finalCta) && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Card className="bg-gradient-to-br from-accent/10 via-card/80 to-card/80 border-accent/20">
              <CardContent className="p-6 md:p-8 grid lg:grid-cols-[1fr_360px] gap-6 items-center">
                <div className="space-y-3">
                  <Badge variant="secondary">{finalCta.badge_text || 'Финальный шаг'}</Badge>
                  <h2 className="text-2xl md:text-3xl font-bold">{finalCta.title || 'Готовы обсудить и быстро запустить решение?'}</h2>
                  <p className="text-muted-foreground">{finalCta.text || 'Оставьте заявку, чтобы получить точный состав, коммерческие условия и следующий шаг по внедрению.'}</p>
                </div>

                {submitSuccess ? (
                  <div className="rounded-2xl bg-accent/10 border border-accent/30 p-6 text-center">
                    <p className="text-xl font-medium text-accent">Заявка отправлена!</p>
                    <p className="text-sm text-muted-foreground mt-2">Мы свяжемся с вами в ближайшее время</p>
                  </div>
                ) : (
                  <div className="lg:justify-self-end w-full space-y-4">
                    <div className="rounded-2xl border border-border/40 bg-background/70 p-4">
                      <div className="flex gap-2 mb-3">
                        <Button
                          type="button"
                          size="sm"
                          variant={contactForm.contactType === 'email' ? 'default' : 'outline'}
                          onClick={() => setContactForm(prev => ({ ...prev, contactType: 'email' }))}
                        >
                          Email
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={contactForm.contactType === 'phone' ? 'default' : 'outline'}
                          onClick={() => setContactForm(prev => ({ ...prev, contactType: 'phone' }))}
                        >
                          Телефон
                        </Button>
                      </div>
                      <div className="space-y-2 mb-3">
                        <Label className="text-xs text-muted-foreground">
                          {contactForm.contactType === 'email' ? 'Email' : 'Телефон'}
                        </Label>
                        <Input
                          type={contactForm.contactType === 'email' ? 'email' : 'tel'}
                          placeholder={contactForm.contactType === 'email' ? 'email@example.com' : '+7 (999) 000-00-00'}
                          value={contactForm.contact}
                          onChange={(e) => setContactForm(prev => ({ ...prev, contact: e.target.value }))}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-11 font-medium" disabled={isSubmitting} onClick={handleSubmitRequest}>
                        {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProductPageSolutionCopy;
